import React, {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "src/shadcn/lib/utils";
import { Typography } from "src/components/Typography";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/primitives/HoverCard";

type PopoverPosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end"
  | "auto";

const popoverVariants = cva("flex flex-col gap-2 p-4", {
  variants: {
    variant: {
      default: "",
      primary: "bg-primary text-primary-foreground ring-0 shadow-lg",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type PopoverVariant = NonNullable<
  VariantProps<typeof popoverVariants>["variant"]
>;

/** Radix HoverCard.Content props we forward via ...otherProps. */
type HoverCardContentProps = React.ComponentProps<typeof HoverCardContent>;

export interface PopoverProps extends Omit<
  HoverCardContentProps,
  "children" | "className" | "side" | "align"
> {
  /** Content rendered inside the popover. */
  children?: ReactNode;
  /** The trigger element that opens the popover on hover. */
  trigger?: ReactNode;
  /** Color variant of the popover. */
  variant?: PopoverVariant;
  /** Disable the popover. */
  disabled?: boolean;
  /** Placement of the popover relative to the trigger. */
  position?: PopoverPosition;
  /** Additional CSS class names for the popover content. */
  className?: string;
  /** Whether the popover is open (controlled). */
  open?: boolean;
  /** Callback when open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Auto-hide after N milliseconds. Use -1 to disable. */
  hideAfter?: number;
  /** Radix HoverCard `defaultOpen` prop. */
  defaultOpen?: boolean;
  /** Delay in ms before the popover opens on hover. */
  openDelay?: number;
  /** Delay in ms before the popover closes after hover leaves. */
  closeDelay?: number;
}

import { SIDE_MAP, ALIGN_MAP } from "./constants";

interface PopoverTitleProps extends React.ComponentProps<"div"> {
  children?: ReactNode;
  className?: string;
}

const Title = forwardRef<HTMLDivElement, PopoverTitleProps>(
  ({ children, className, ...otherProps }, ref) => (
    <Typography variant="body2" weight="semibold" color="inherit" asChild>
      <div
        ref={ref}
        data-slot="popover-title"
        className={className}
        {...otherProps}
      >
        {children}
      </div>
    </Typography>
  )
);
Title.displayName = "Popover.Title";

const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      children,
      trigger,
      variant = "default",
      disabled = false,
      position = "auto",
      className,
      open: openProp,
      onOpenChange: onOpenChangeProp,
      hideAfter = -1,
      defaultOpen,
      openDelay = 200,
      closeDelay = 200,
      // Radix HoverCardContent props forwarded
      ...contentProps
    },
    ref
  ) => {
    // Internal state for uncontrolled mode + hideAfter
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = openProp !== undefined;
    const open = isControlled ? openProp : internalOpen;

    const handleOpenChange = useCallback(
      (next: boolean) => {
        if (!isControlled) setInternalOpen(next);
        onOpenChangeProp?.(next);
      },
      [isControlled, onOpenChangeProp]
    );

    // Auto-hide after hideAfter ms
    useEffect(() => {
      if (!open || hideAfter <= 0) return;
      const timer = setTimeout(() => handleOpenChange(false), hideAfter);
      return () => clearTimeout(timer);
    }, [open, hideAfter, handleOpenChange]);

    if (disabled) {
      return <>{trigger}</>;
    }

    const side = SIDE_MAP[position] ?? "bottom";
    const align = ALIGN_MAP[position] ?? "center";

    // Wrap non-element trigger in a span
    const triggerElement = React.isValidElement(trigger) ? (
      trigger
    ) : (
      <span>{trigger}</span>
    );

    return (
      <HoverCard
        open={open}
        onOpenChange={handleOpenChange}
        defaultOpen={defaultOpen}
        openDelay={openDelay}
        closeDelay={closeDelay}
      >
        <HoverCardTrigger asChild>{triggerElement}</HoverCardTrigger>
        <HoverCardContent
          ref={ref}
          side={side}
          align={align}
          className={cn(popoverVariants({ variant }), className)}
          {...contentProps}
        >
          {children}
        </HoverCardContent>
      </HoverCard>
    );
  }
) as React.ForwardRefExoticComponent<
  PopoverProps & React.RefAttributes<HTMLDivElement>
> & {
  Title: typeof Title;
};

Popover.displayName = "Popover";
Popover.Title = Title;

export { Popover };
